import { useMutation } from '@apollo/client';
import type {
  InsertAssessmentInput,
  InsertOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  GetAllOversightAssessmentsDocument,
  GetOversightAssessmentByIdDocument,
  InsertOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertOversightAssessmentTRPC } from './useInsertOversightAssessmentTRPC';

export const useInsertOversightAssessment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertOversightAssessmentGraphQL, graphqlState] = useMutation(
    InsertOversightAssessmentDocument,
    {
      update: (cache) => {
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'oversight_assessment_aggregate');
      },
      refetchQueries: [
        GetOversightAssessmentByIdDocument,
        GetAllOversightAssessmentsDocument,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertOversightAssessmentTRPC();

  const insertOversightAssessment = async (
    variables: InsertAssessmentInput
  ): Promise<InsertOversightAssessmentMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertOversightAssessment({
        ...variables,
        Title: variables.Title ?? '',
        Summary: variables.Summary ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
    }

    const result = await insertOversightAssessmentGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert oversight assessment');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertOversightAssessment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertOversightAssessment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
